import PropTypes from 'prop-types';
import styles from './FJImageExtenderCreations.module.less';
import { useRef, useEffect, useState } from 'react';
import FJImageExtenderPreviewCanvas from './FJImageExtenderPreviewCanvas';
import FJImageExtenderEdit from './FJImageExtenderEdit';
import { useCallback } from 'react';
import { useMemo } from 'react';
import FJSlider from '../../../../common/components/FJSlider/FJSlider';
import FJZoomIcon from '../../../../assets/svg/timeline-zoom-.svg?react';
import FJZoomAddIcon from '../../../../assets/svg/timeline-zoom+.svg?react';
import FJToolTip from '../../../../common/components/FJToolTip/FJToolTip';
import classNames from 'classnames';
import FJLocalStore from '../../../../store/FJLocalStore';

const FIT_MAX = 3;
const FIT_MIN = 0.1;

const FJImageExtenderCreations = props => {
    const { active, uploadImageInfo, boardSize, imageRect, setImageRect, curRatioName } = props;

    const [previewSize, setPreviewSize] = useState({ width: 0, height: 0 });
    const [mouseIn, setMouseIn] = useState(false);
    const [fit, setFit] = useState(1);

    const previewBoxRef = useRef(null);

    const boardRect = useMemo(() => {
        const boardRatio = boardSize.width / boardSize.height;
        const borderRect = { width: 0, height: 0, x: 0, y: 0 };

        if (boardRatio > 1) {
            borderRect.width = Math.min(previewSize.width, previewSize.height * boardRatio);
            borderRect.height = borderRect.width / boardRatio;
            borderRect.x = (previewSize.width - borderRect.width) / 2;
            borderRect.y = (previewSize.height - borderRect.height) / 2;
        } else {
            borderRect.height = Math.min(previewSize.height, previewSize.width / boardRatio);
            borderRect.width = borderRect.height * boardRatio;
            borderRect.x = (previewSize.width - borderRect.width) / 2;
            borderRect.y = (previewSize.height - borderRect.height) / 2;
        }
        return borderRect;
    }, [boardSize, previewSize]);

    const stateImageRect = useMemo(() => {
        const imageRatio = imageRect.width / imageRect.height;
        const curImageRect = { width: 0, height: 0, x: 0, y: 0 };

        if (imageRatio > 1) {
            curImageRect.width = boardRect.width * (imageRect.width / boardSize.width);
            curImageRect.height = curImageRect.width / imageRatio;
            curImageRect.x = boardRect.width * (imageRect.x / boardSize.width) + boardRect.x;
            curImageRect.y = boardRect.height * (imageRect.y / boardSize.height) + boardRect.y;
        } else {
            curImageRect.height = boardRect.height * (imageRect.height / boardSize.height);
            curImageRect.width = curImageRect.height * imageRatio;
            curImageRect.x = boardRect.width * (imageRect.x / boardSize.width) + boardRect.x;
            curImageRect.y = boardRect.height * (imageRect.y / boardSize.height) + boardRect.y;
        }

        return curImageRect;
    }, [imageRect, boardRect, boardSize]);

    const resizeRect = useCallback(() => {
        const previewBox = previewBoxRef.current;

        setPreviewSize({ width: previewBox.clientWidth, height: previewBox.clientHeight });
    }, [previewBoxRef]);

    useEffect(() => {
        active && resizeRect();
    }, [active, resizeRect]);

    useEffect(() => {
        setFit(1);
    }, [curRatioName]);

    useEffect(() => {
        resizeRect();
        window.addEventListener('resize', resizeRect);

        return () => {
            window.removeEventListener('resize', resizeRect);
        };
    }, [resizeRect]);

    const changeImageRect = useCallback(
        (rect, direction) => {
            const newRect = {
                width: (rect.width / boardRect.width) * boardSize.width,
                height: (rect.height / boardRect.height) * boardSize.height,
                x: ((rect.x - boardRect.x) / boardRect.width) * boardSize.width,
                y: ((rect.y - boardRect.y) / boardRect.height) * boardSize.height,
            };

            setImageRect(newRect, direction);
        },
        [setImageRect, boardRect, boardSize],
    );

    const handleWheel = useCallback(event => {
        if (event.deltaY < 0) {
            setFit(prevFit => Math.min(prevFit + 0.1, FIT_MAX));
        } else {
            setFit(prevFit => Math.max(prevFit - 0.1, FIT_MIN));
        }
    }, []);

    useEffect(() => {
        if (active && mouseIn) {
            window.addEventListener('wheel', handleWheel);
        } else {
            window.removeEventListener('wheel', handleWheel);
        }
    }, [active, handleWheel, mouseIn]);

    return (
        <div
            className={styles.content}
            style={{ display: active ? 'block' : 'none' }}
            onMouseEnter={() => setMouseIn(true)}
            onMouseLeave={() => setMouseIn(false)}
        >
            <div className={styles.preview_box} ref={previewBoxRef} style={{ transform: `scale(${fit})` }}>
                <FJImageExtenderPreviewCanvas
                    imageRect={stateImageRect}
                    boardRect={boardRect}
                    previewSize={previewSize}
                    uploadImageInfo={uploadImageInfo}
                />
                <FJImageExtenderEdit
                    imageRect={stateImageRect}
                    boardRect={boardRect}
                    changeImageRect={changeImageRect}
                />
            </div>
            <div className={styles.fit_box}>
                <FJToolTip title='image-extender-zoom-out'>
                    <div
                        className={classNames(styles.fit_icon_box, {
                            [styles.disabled]: fit <= FIT_MIN,
                        })}
                        onClick={() => setFit(prevFit => Math.max(prevFit - 0.1, FIT_MIN))}
                    >
                        <FJZoomIcon className={styles.fit_icon} />
                    </div>
                </FJToolTip>
                <FJSlider className={styles.fit_slider} max={FIT_MAX} min={FIT_MIN} value={fit} onChange={setFit} />
                <FJToolTip title='image-extender-zoom-in'>
                    <div
                        className={classNames(styles.fit_icon_box, {
                            [styles.disabled]: fit >= FIT_MAX,
                        })}
                        onClick={() => setFit(prevFit => Math.min(prevFit + 0.1, FIT_MAX))}
                    >
                        <FJZoomAddIcon className={styles.fit_icon} />
                    </div>
                </FJToolTip>
                <div className={classNames(styles.fit_btn, { [styles.disabled]: fit === 1 })} onClick={() => setFit(1)}>
                    {FJLocalStore._('fit')}
                </div>
            </div>

            <div className={styles.board_info}>
                {FJLocalStore._('current-pixel')}: {boardSize.width} * {boardSize.height} px
            </div>
        </div>
    );
};

FJImageExtenderCreations.propTypes = {
    active: PropTypes.bool,
    uploadImageInfo: PropTypes.object,
    boardSize: PropTypes.object,
    imageRect: PropTypes.object,
    setImageRect: PropTypes.func,
    curRatioName: PropTypes.string,
};

export default FJImageExtenderCreations;
